import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch, connect } from "react-redux";
import "./AIAlerts.scss";
import TradeAlgoTable from "../common/Tables/TradeAlgoTable";
import { getAILiveAlertsColumns } from "./aiAlertsColumns";
import {
  getLiveAlertsData,
  getLiveAlertsLoading,
  liveAlertsLoad,
} from "../../appRedux/ducks/liveAlerts";
import { showSnackbar } from "../../appRedux/ducks/snackbar";
import moment from "moment-timezone";

/**
 * Format date string from "2025-10-10" to "FRIDAY, Oct 17, 2025" (matching Figma design)
 * Uses UTC to match the payload dates exactly
 */
const formatDateGroup = (dateString) => {
  if (!dateString) return "";
  const date = moment.utc(dateString);
  const weekdays = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const weekday = weekdays[date.day()];
  const month = months[date.month()];
  const day = date.date();
  const year = date.year();

  return `${weekday}, ${month} ${day}, ${year}`;
};

/**
 * Format last updated timestamp to "Last updated: Thursday, Nov 28, 2024 at 1:23 PM (EST)"
 * Uses EST timezone to match Figma design
 */
const formatLastUpdated = (timestamp) => {
  if (!timestamp) return "";
  const date = moment.tz(timestamp, "America/New_York");
  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const weekday = weekdays[date.day()];
  const month = months[date.month()];
  const day = date.date();
  const year = date.year();
  const time = date.format("h:mm A");

  return `Last updated: ${weekday}, ${month} ${day}, ${year} at ${time} (EST)`;
};

/**
 * Get today's date formatted for empty state
 * Uses UTC to match the payload dates exactly
 */
const getTodayDateGroup = () => {
  const today = moment.utc();
  const year = today.year();
  const month = String(today.month() + 1).padStart(2, "0");
  const day = String(today.date()).padStart(2, "0");
  const dateString = `${year}-${month}-${day}`;
  return formatDateGroup(dateString);
};

const LiveAlerts = ({
  themeMode,
  onShowDetails,
  onLatestActivityDateChange,
}) => {
  const dispatch = useDispatch();
  // Store sort models per date group to scope sorting to individual tables
  const [sortModels, setSortModels] = useState({});

  // Redux selectors
  const alertGroups = useSelector(getLiveAlertsData);
  const loading = useSelector(getLiveAlertsLoading);

  // Load live alerts on mount
  useEffect(() => {
    dispatch(liveAlertsLoad());
  }, [dispatch]);

  const handleCopyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        dispatch(showSnackbar("Copied to clipboard", { variant: "success" }));
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        dispatch(
          showSnackbar("Failed to copy to clipboard", { variant: "error" }),
        );
      });
  };

  const handleSeeDetails = (row) => {
    console.log("See details for:", row);
    if (onShowDetails) {
      onShowDetails(row.id);
    }
  };

  // Handler to update sort model for a specific date group
  const handleSortModelChange = (dateGroup, newSortModel) => {
    setSortModels((prev) => ({
      ...prev,
      [dateGroup]: newSortModel,
    }));
  };

  // Get shared column definitions
  const columns = getAILiveAlertsColumns(
    handleCopyToClipboard,
    handleSeeDetails,
  );

  // Cutoff date: November 6, 2025 (pre-launch trades cutoff)
  const PRE_LAUNCH_CUTOFF_DATE = moment.utc("2025-11-06").endOf("day");

  // Map alertGroups from API to grouped format for rendering
  // Separate alerts into pre-launch (before Nov 6) and forward tested (after Nov 6) groups
  // Group alerts by their aiBuyDate (alert sent date) instead of exit date
  const { preLaunchGroups, forwardTestedGroups } = useMemo(() => {
    const preLaunchGroups = {};
    const forwardTestedGroups = {};

    alertGroups.forEach((alertGroup) => {
      (alertGroup.alerts || []).forEach((alert) => {
        // Use aiBuyDate to determine which date group this alert belongs to
        // aiBuyDate represents when the alert was sent out (correct behavior)
        // Fallback to alertGroup.date only if aiBuyDate is missing (shouldn't happen for live alerts)
        const alertDate = alert.aiBuyDate || alertGroup.date;
        if (!alertDate) return; // Skip alerts without any date

        // Determine if this alert is pre-launch or forward tested
        const tradeDate = moment.utc(alertDate).startOf("day");
        const isPreLaunch = tradeDate.isBefore(PRE_LAUNCH_CUTOFF_DATE);

        const dateGroup = formatDateGroup(alertDate);

        // Group by dateGroup within the appropriate category
        const targetGroups = isPreLaunch
          ? preLaunchGroups
          : forwardTestedGroups;

        if (!targetGroups[dateGroup]) {
          targetGroups[dateGroup] = [];
        }
        targetGroups[dateGroup].push(alert);
      });
    });
    return { preLaunchGroups, forwardTestedGroups };
  }, [alertGroups]);

  // Calculate latest activity date from all alerts
  // Uses the NEWEST date value available (aiBuyDate or aiExitDate, whichever is newer)
  const latestActivityDate = useMemo(() => {
    const allDates = [];
    alertGroups.forEach((alertGroup) => {
      (alertGroup.alerts || []).forEach((alert) => {
        // Consider both aiBuyDate and aiExitDate, use whichever is newer
        const dates = [];
        if (alert.aiBuyDate) {
          dates.push(moment.utc(alert.aiBuyDate));
        }
        if (alert.aiExitDate) {
          dates.push(moment.utc(alert.aiExitDate));
        }
        if (alert.lastUpdatedTimestamp && dates.length === 0) {
          dates.push(moment.utc(alert.lastUpdatedTimestamp));
        }

        // Get the newest date for this alert
        if (dates.length > 0) {
          const newestDateForAlert = moment.max(dates);
          allDates.push(newestDateForAlert);
        }
      });
    });

    if (allDates.length === 0) {
      // If no data, return current time in EST
      return formatLastUpdated(moment.tz("America/New_York").toISOString());
    }

    // Find the latest date across all alerts
    const latestDate = moment.max(allDates);
    return formatLastUpdated(latestDate.toISOString());
  }, [alertGroups]);

  // Notify parent component of latest activity date change
  useEffect(() => {
    if (onLatestActivityDateChange) {
      onLatestActivityDateChange(latestActivityDate);
    }
  }, [latestActivityDate, onLatestActivityDateChange]);

  // Check if there are any alerts (not loading and no groups)
  const totalGroups =
    Object.keys(preLaunchGroups).length +
    Object.keys(forwardTestedGroups).length;
  const hasNoAlerts = !loading && totalGroups === 0;
  const todayDateGroup = getTodayDateGroup();

  return (
    <div className="live-alerts-content">
      <div className="ai-alerts-table-container">
        {hasNoAlerts ? (
          // Empty state: Show today's date with "NO trades detected" message (matching Figma design)
          <div className="ai-alerts-date-group-section">
            <div className="ai-alerts-date-group">
              <span className="date-group-text">{todayDateGroup}</span>
            </div>
            <div className="ai-alerts-empty-state-row">
              <p className="ai-alerts-empty-state-text">NO trades detected</p>
            </div>
          </div>
        ) : (
          <>
            {/* Forward Tested Trades Section (after Nov 6) */}
            {Object.keys(forwardTestedGroups).length > 0 && (
              <>
                {Object.entries(forwardTestedGroups).map(
                  ([dateGroup, alerts], index) => (
                    <div
                      key={`forward-${index}`}
                      className="ai-alerts-date-group-section"
                    >
                      <div className="ai-alerts-date-group">
                        <span className="date-group-text">{dateGroup}</span>
                      </div>
                      <TradeAlgoTable
                        rows={alerts}
                        columns={columns}
                        loading={loading}
                        sortModel={sortModels[dateGroup] || []}
                        setSortModel={(newSortModel) =>
                          handleSortModelChange(dateGroup, newSortModel)
                        }
                        tableClassName="ai-alerts-table"
                        hasRowClassName={false}
                        customToolbar={false}
                        threshold={0}
                      />
                    </div>
                  ),
                )}
              </>
            )}

            {/* Separator between Forward Tested and Pre-Launch */}
            {/* Show separator whenever there are pre-launch trades */}
            {Object.keys(preLaunchGroups).length > 0 && (
              <div className="ai-alerts-separator">
                <span className="separator-text">
                  PRE-LAUNCH TRADES (BEFORE NOV 6)
                </span>
              </div>
            )}

            {/* Pre-Launch Trades Section (before Nov 6) */}
            {Object.keys(preLaunchGroups).length > 0 && (
              <>
                {Object.entries(preLaunchGroups).map(
                  ([dateGroup, alerts], index) => (
                    <div
                      key={`prelaunch-${index}`}
                      className="ai-alerts-date-group-section"
                    >
                      <div className="ai-alerts-date-group">
                        <span className="date-group-text">{dateGroup}</span>
                      </div>
                      <TradeAlgoTable
                        rows={alerts}
                        columns={columns}
                        loading={loading}
                        sortModel={sortModels[dateGroup] || []}
                        setSortModel={(newSortModel) =>
                          handleSortModelChange(dateGroup, newSortModel)
                        }
                        tableClassName="ai-alerts-table"
                        hasRowClassName={false}
                        customToolbar={false}
                        threshold={0}
                      />
                    </div>
                  ),
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
});

export default connect(stateToProps)(LiveAlerts);
